import type { CSSProperties } from 'react'

import { cn } from '@morpho-org/ui-v6/utils'
import { Icon } from '@repo/ui'
import { Morpho, MorphoGradient } from '@repo/ui/icons'
import { forwardRef } from 'react'

import { Card, type CardProps } from './Card'

export interface CardTextProps extends Omit<CardProps, 'children'> {
  /** The message text to display */
  text: string
  /** @deprecated Use className with Tailwind height utilities instead (e.g., `className="h-[20px]"`) Height of the card container */
  height?: CSSProperties['height']
  /** @deprecated Use className with Tailwind width utilities instead (e.g., `className="w-full"`) Width of the card container */
  width?: CSSProperties['width']
}

/**
 * CardText component displays a message with the Morpho logo icon.
 * Used to show messages when there is no data or as an empty state.
 *
 * The icon automatically switches between gradient (light mode) and plain (dark mode).
 *
 * @example
 * ```tsx
 * <CardText text="No data available" />
 * <CardText text="Loading..." height="200px" />
 * ```
 */
const CardText = forwardRef<HTMLDivElement, CardTextProps>(
  ({ text, height, width, className, style, ...props }, ref) => {
    return (
      <Card
        ref={ref}
        className={cn(
          'gap-sm flex h-[400px] w-full flex-col items-center justify-center',
          className
        )}
        // style is deprecated for retrocompatibility with v5
        style={{
          height,
          width,
          ...style
        }}
        {...props}
      >
        {/* Light mode icon - hidden in dark mode */}
        <Icon icon={<MorphoGradient />} className="size-[28px] dark:hidden" />
        {/* Dark mode icon - hidden in light mode */}
        <Icon icon={<Morpho />} className="hidden size-[28px] dark:block" />
        <span className="text-2xs text-muted-foreground">{text}</span>
      </Card>
    )
  }
)

CardText.displayName = 'CardText'

export { CardText }
